import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase, withTimeout } from "@/lib/hooks/api/utils";
import { Tickets, TicketsUpdateInput } from "@/src/gql/graphql";
import { TicketsUpdateInputSchema } from "@/src/gql/validation";
import { UpdateTicketContext } from "./tickets-types";

export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TicketsUpdateInput }) => {
      const validatedData = TicketsUpdateInputSchema.parse(updates);

      const supabase = getSupabase();
      const { data, error } = await withTimeout(
        supabase.from("tickets").update(validatedData).eq("id", id).select().single(),
        15000,
        "update ticket"
      );

      if (error) {
        console.error("Error updating ticket:", error);
        throw new Error(error.message);
      }

      if (
        validatedData.description !== undefined &&
        typeof data?.id === "string"
      ) {
        fetch(`/api/tickets/${data.id}/description?resetPointers=1`, {
          method: "POST",
          credentials: "include",
        }).catch((offloadError) => {
          console.error(
            "Failed to queue ticket description offload:",
            offloadError
          );
        });
      }

      return data as Tickets;
    },
    onMutate: async ({ id, updates }): Promise<UpdateTicketContext> => {
      await queryClient.cancelQueries({ queryKey: ["tickets"] });
      await queryClient.cancelQueries({ queryKey: ["ticket", id] });
      await queryClient.cancelQueries({ queryKey: ["tickets-paginated"] });

      const previousTickets = queryClient.getQueryData<Tickets[]>(["tickets"]);
      const previousTicket = queryClient.getQueryData<Tickets>(["ticket", id]);
      const previousPaginated = queryClient.getQueryData<{ data: Tickets[] }>([
        "tickets-paginated",
      ]);

      queryClient.setQueryData(["ticket", id], (old: Tickets | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      queryClient.setQueryData(["tickets"], (old: Tickets[] | undefined) => {
        if (!old) return old;
        return old.map((ticket) => (ticket.id === id ? { ...ticket, ...updates } : ticket));
      });

      queryClient.setQueryData(["tickets-paginated"], (old: { data: Tickets[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((ticket: Tickets) =>
            ticket.id === id ? { ...ticket, ...updates } : ticket
          ),
        };
      });

      return { previousTickets, previousTicket, previousPaginated };
    },
    onSuccess: (data: Tickets) => {
      queryClient.setQueryData(["ticket", data.id], data);

      queryClient.setQueryData(["tickets"], (old: Tickets[] | undefined) => {
        if (!old) return old;
        return old.map((ticket) => (ticket.id === data.id ? data : ticket));
      });

      queryClient.setQueryData(["tickets-paginated"], (old: { data: Tickets[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((ticket: Tickets) => (ticket.id === data.id ? data : ticket)),
        };
      });

      if (data?.tags) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "tickets"] });
        fetch("/api/tags/invalidate/tickets", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side ticket tags cache:", error);
        });
      }

      toast.success("Ticket updated successfully");
    },
    onError: (error, _variables, onMutateResult: UpdateTicketContext | undefined) => {
      if (onMutateResult?.previousTickets) {
        queryClient.setQueryData(["tickets"], onMutateResult.previousTickets);
      }
      if (onMutateResult?.previousTicket) {
        queryClient.setQueryData(["ticket", _variables.id], onMutateResult.previousTicket);
      }
      if (onMutateResult?.previousPaginated) {
        queryClient.setQueryData(["tickets-paginated"], onMutateResult.previousPaginated);
      }

      toast.error("Failed to update ticket");
      console.error("Error updating ticket:", error);
    },
  });
}
